// Senior French Student Notes - Years 11-13
// Comprehensive study materials for French as a Foreign Language

export const year11FrenchStudentNotes = {
  year: 11,
  subject: "French as a Foreign Language",
  strands: {
    strand1: {
      name: "Expression orale",
      units: [
        {
          id: "ffl1-1",
          name: "Écouter et répondre",
          objectives: [
            "Comprendre des discours longs (radio, télévision, actualités)",
            "Extraire les idées principales des documents audio-visuels",
            "Identifier les mots difficiles dans leur contexte"
          ],
          keyTerms: [
            { term: "L'écoute active", definition: "Active listening - paying full attention to understand the message" },
            { term: "Les idées principales", definition: "Main ideas - the most important points in a text or speech" },
            { term: "Le contexte", definition: "Context - the situation or background that helps understand meaning" },
            { term: "Résumer", definition: "To summarize - to give a brief statement of the main points" },
            { term: "Les actualités", definition: "Current events/news" }
          ],
          studyNotes: `## Compétences d'écoute (Listening Skills)

### Stratégies pour bien écouter
1. **Avant l'écoute** - Before listening
   - Regardez les images et le titre
   - Pensez à ce que vous savez sur le sujet
   - Préparez des questions

2. **Pendant l'écoute** - During listening
   - Concentrez-vous sur les mots-clés
   - Ne vous arrêtez pas sur les mots inconnus
   - Notez les idées principales

3. **Après l'écoute** - After listening
   - Résumez ce que vous avez compris
   - Vérifiez vos réponses
   - Posez des questions

### Expressions utiles
- "Je comprends que..." - I understand that...
- "L'idée principale est..." - The main idea is...
- "Le locuteur dit que..." - The speaker says that...
- "Selon l'émission..." - According to the program...`,
          practiceQuestions: [
            "Quelles sont les trois étapes pour une bonne écoute?",
            "Comment peut-on identifier les idées principales?",
            "Pourquoi est-il important de regarder les images avant d'écouter?",
            "Que faire si vous ne comprenez pas un mot?",
            "Comment résumer un texte oral?"
          ],
          examTips: "Écoutez toujours deux fois. La première fois pour comprendre le sens général, la deuxième fois pour les détails."
        },
        {
          id: "ffl1-2",
          name: "Débattre et prendre la parole",
          objectives: [
            "Participer aux discussions en groupe",
            "Raconter des expériences personnelles",
            "Partager des informations et des idées"
          ],
          keyTerms: [
            { term: "Un débat", definition: "A debate - a formal discussion on a topic" },
            { term: "Une opinion", definition: "An opinion - what someone thinks about something" },
            { term: "Un argument", definition: "An argument - a reason given to support an opinion" },
            { term: "Être d'accord", definition: "To agree" },
            { term: "Ne pas être d'accord", definition: "To disagree" }
          ],
          studyNotes: `## Prendre la parole (Speaking Skills)

### Expressions pour donner son opinion
- "À mon avis..." - In my opinion...
- "Je pense que..." - I think that...
- "Je crois que..." - I believe that...
- "Selon moi..." - According to me...
- "Il me semble que..." - It seems to me that...

### Expressions pour être d'accord
- "Je suis d'accord" - I agree
- "C'est vrai" - That's true
- "Exactement" - Exactly
- "Tu as raison" - You're right

### Expressions pour ne pas être d'accord
- "Je ne suis pas d'accord" - I don't agree
- "Ce n'est pas vrai" - That's not true
- "Au contraire..." - On the contrary...
- "Cependant..." - However...

### Raconter une expérience
- "D'abord..." - First...
- "Ensuite..." - Then...
- "Après..." - After...
- "Finalement..." - Finally...`,
          practiceQuestions: [
            "Comment peut-on exprimer son opinion en français?",
            "Quelles expressions utilise-t-on pour être d'accord?",
            "Comment raconter une histoire dans l'ordre chronologique?",
            "Donnez trois expressions pour ne pas être d'accord poliment.",
            "Préparez un court discours sur vos vacances préférées."
          ],
          examTips: "Utilisez des connecteurs logiques pour organiser vos idées: d'abord, ensuite, puis, enfin."
        }
      ]
    },
    strand2: {
      name: "Lire et répondre",
      units: [
        {
          id: "ffl2-1",
          name: "Compétences de lecture",
          objectives: [
            "Utiliser des stratégies de lecture efficaces",
            "Identifier l'auteur, le cadre, le thème, les personnages",
            "Lire à haute voix avec expression"
          ],
          keyTerms: [
            { term: "Le titre", definition: "The title" },
            { term: "L'auteur", definition: "The author" },
            { term: "Le personnage", definition: "The character" },
            { term: "Le thème", definition: "The theme - the main subject" },
            { term: "Le cadre", definition: "The setting - where and when a story takes place" }
          ],
          studyNotes: `## Stratégies de lecture

### Avant de lire
1. Regardez le titre - What does it suggest?
2. Observez les images - What can you predict?
3. Lisez la première phrase - What is the topic?

### Pendant la lecture
1. Identifiez les mots-clés
2. Cherchez les idées principales
3. Notez les mots inconnus
4. Utilisez le contexte pour deviner le sens

### Types de textes
| Type | Caractéristiques |
|------|------------------|
| Narratif | Raconte une histoire, a des personnages |
| Descriptif | Décrit une personne, un lieu, une chose |
| Informatif | Donne des informations, des faits |
| Argumentatif | Présente des opinions, des arguments |

### Éléments d'un texte narratif
- **Qui?** - Les personnages
- **Où?** - Le lieu
- **Quand?** - Le temps
- **Quoi?** - L'action/l'intrigue
- **Pourquoi?** - Le motif`,
          practiceQuestions: [
            "Quels éléments regardez-vous avant de lire un texte?",
            "Comment peut-on identifier le thème d'un texte?",
            "Quelle est la différence entre un texte narratif et un texte informatif?",
            "Comment deviner le sens d'un mot inconnu?",
            "Quels sont les éléments d'une histoire (qui, où, quand, quoi)?"
          ],
          examTips: "Lisez les questions avant de lire le texte pour savoir ce qu'il faut chercher."
        }
      ]
    },
    strand3: {
      name: "Composition",
      units: [
        {
          id: "ffl3-1",
          name: "Rédaction de base",
          objectives: [
            "Générer des idées pour l'écriture",
            "Organiser les idées en paragraphes",
            "Utiliser des structures de texte appropriées"
          ],
          keyTerms: [
            { term: "L'introduction", definition: "The introduction - the beginning that presents the topic" },
            { term: "Le développement", definition: "The body - the main part with details" },
            { term: "La conclusion", definition: "The conclusion - the ending that summarizes" },
            { term: "Un paragraphe", definition: "A paragraph - a group of sentences about one idea" },
            { term: "Une phrase", definition: "A sentence" }
          ],
          studyNotes: `## La rédaction (Writing)

### Structure d'une composition
1. **Introduction** (1 paragraphe)
   - Présente le sujet
   - Attire l'attention du lecteur
   - Annonce le plan

2. **Développement** (2-3 paragraphes)
   - Développe les idées principales
   - Un paragraphe = une idée
   - Utilise des exemples

3. **Conclusion** (1 paragraphe)
   - Résume les idées principales
   - Donne une opinion finale
   - Termine de façon mémorable

### Connecteurs logiques
| Fonction | Exemples |
|----------|----------|
| Addition | et, aussi, de plus, en outre |
| Opposition | mais, cependant, pourtant |
| Cause | parce que, car, puisque |
| Conséquence | donc, alors, ainsi |
| Temps | d'abord, ensuite, enfin |

### Types d'écriture (Year 11)
- Lettres informelles
- Histoires courtes
- Descriptions
- Instructions`,
          practiceQuestions: [
            "Quelles sont les trois parties d'une composition?",
            "Combien d'idées met-on dans un paragraphe?",
            "Donnez trois connecteurs pour l'addition.",
            "Comment commence-t-on une introduction?",
            "Écrivez une lettre informelle à un ami."
          ],
          examTips: "Planifiez votre composition avant d'écrire. Faites un brouillon avec les idées principales."
        }
      ]
    },
    strand4: {
      name: "Étude de la langue",
      units: [
        {
          id: "ffl4-1",
          name: "Les temps des verbes",
          objectives: [
            "Utiliser le présent, le passé composé, l'imparfait et le futur",
            "Identifier les parties du discours",
            "Construire des phrases correctes"
          ],
          keyTerms: [
            { term: "Le présent", definition: "Present tense - actions happening now or habits" },
            { term: "Le passé composé", definition: "Past tense for completed actions" },
            { term: "L'imparfait", definition: "Past tense for descriptions, habits, ongoing actions" },
            { term: "Le futur simple", definition: "Future tense - actions that will happen" },
            { term: "Le verbe", definition: "The verb - an action or state word" }
          ],
          studyNotes: `## Les temps des verbes

### Le présent (Present tense)
**Usage:** Actions actuelles, habitudes, vérités générales

**Verbes réguliers -ER:** (parler)
je parle, tu parles, il/elle parle
nous parlons, vous parlez, ils/elles parlent

**Verbes réguliers -IR:** (finir)
je finis, tu finis, il/elle finit
nous finissons, vous finissez, ils/elles finissent

### Le passé composé (Past - completed actions)
**Formation:** avoir/être + participe passé

**Exemples:**
- J'ai mangé (I ate)
- Il est allé (He went)
- Nous avons vu (We saw)

### L'imparfait (Past - descriptions/habits)
**Formation:** Radical + -ais, -ais, -ait, -ions, -iez, -aient

**Usage:**
- Descriptions: "Il faisait beau" (It was nice)
- Habitudes: "Je jouais tous les jours" (I used to play every day)

### Le futur simple (Future)
**Formation:** Infinitif + -ai, -as, -a, -ons, -ez, -ont

**Exemples:**
- Je parlerai (I will speak)
- Nous finirons (We will finish)`,
          practiceQuestions: [
            "Conjuguez le verbe 'parler' au présent.",
            "Quelle est la différence entre le passé composé et l'imparfait?",
            "Mettez au futur: 'Je mange une pomme.'",
            "Quand utilise-t-on le passé composé?",
            "Complétez avec le temps correct: 'Hier, il ____ (faire) beau et nous ____ (aller) à la plage.'"
          ],
          examTips: "Apprenez les verbes irréguliers les plus courants: être, avoir, aller, faire, venir."
        }
      ]
    }
  }
};

export const year12FrenchStudentNotes = {
  year: 12,
  subject: "French as a Foreign Language",
  strands: {
    strand1: {
      name: "Expression orale avancée",
      units: [
        {
          id: "ffl1-12",
          name: "Compétences orales avancées",
          objectives: [
            "Analyser des documents audio-visuels complexes",
            "Participer à des débats formels",
            "Présenter des recherches oralement"
          ],
          keyTerms: [
            { term: "La présentation", definition: "A presentation - a formal talk on a subject" },
            { term: "La recherche", definition: "Research - investigation and study" },
            { term: "Les sources", definition: "Sources - where information comes from" },
            { term: "L'auditoire", definition: "The audience" },
            { term: "Les supports visuels", definition: "Visual aids" }
          ],
          studyNotes: `## Présentation orale avancée

### Structure d'une présentation
1. **Accroche** - Hook to grab attention
2. **Introduction** - Present the topic
3. **Plan** - Outline what you'll cover
4. **Développement** - Main content (3-4 points)
5. **Conclusion** - Summary and final thought
6. **Questions** - Invite audience questions

### Conseils pour bien présenter
- Maintenez le contact visuel
- Parlez clairement et pas trop vite
- Utilisez des gestes naturels
- Engagez l'auditoire avec des questions
- Utilisez des supports visuels

### Expressions pour structurer
- "Je vais vous parler de..." - I'm going to talk about...
- "Mon exposé porte sur..." - My presentation is about...
- "Premièrement/Deuxièmement..." - Firstly/Secondly...
- "Pour conclure..." - To conclude...
- "Avez-vous des questions?" - Do you have any questions?

### Le débat formel
**Pour donner son avis:**
- "Je suis convaincu(e) que..." - I'm convinced that...
- "Il est évident que..." - It's obvious that...

**Pour réfuter:**
- "Je m'oppose à cette idée..." - I oppose this idea...
- "Cet argument n'est pas valable..." - This argument isn't valid...`,
          practiceQuestions: [
            "Quelles sont les parties d'une bonne présentation?",
            "Comment peut-on engager l'auditoire?",
            "Donnez trois expressions pour structurer un exposé.",
            "Comment réfuter poliment un argument?",
            "Préparez une présentation de 5 minutes sur un sujet de votre choix."
          ],
          examTips: "Pratiquez votre présentation plusieurs fois. Chronométrez-vous pour respecter le temps imparti."
        }
      ]
    },
    strand2: {
      name: "Lecture approfondie",
      units: [
        {
          id: "ffl2-12",
          name: "Compréhension approfondie",
          objectives: [
            "Extraire des informations détaillées des textes",
            "Évaluer le contexte, les personnages et les caractéristiques linguistiques",
            "Résumer des textes complexes"
          ],
          keyTerms: [
            { term: "L'analyse", definition: "Analysis - detailed examination" },
            { term: "Le registre", definition: "Register - level of formality in language" },
            { term: "Le ton", definition: "Tone - the attitude expressed in writing" },
            { term: "L'implicite", definition: "Implicit meaning - what is suggested but not stated" },
            { term: "L'explicite", definition: "Explicit meaning - what is directly stated" }
          ],
          studyNotes: `## Analyse de texte avancée

### Niveaux de sens
1. **Sens littéral** - Ce qui est dit directement
2. **Sens implicite** - Ce qui est suggéré
3. **Sens symbolique** - Ce que les éléments représentent

### Registres de langue
| Registre | Caractéristiques | Exemple |
|----------|------------------|---------|
| Soutenu | Formel, littéraire | "Je vous prie de bien vouloir..." |
| Courant | Normal, standard | "S'il vous plaît, pourriez-vous..." |
| Familier | Informel, oral | "Tu peux...?" |

### Types de questions
- **Repérage:** Trouvez dans le texte...
- **Compréhension:** Expliquez ce que veut dire...
- **Analyse:** Pourquoi l'auteur a-t-il choisi...?
- **Opinion:** Que pensez-vous de...?

### Comment résumer
1. Lisez le texte entièrement
2. Identifiez les idées principales
3. Éliminez les détails et exemples
4. Reformulez avec vos propres mots
5. Vérifiez que le sens est conservé`,
          practiceQuestions: [
            "Quelle est la différence entre le sens littéral et le sens implicite?",
            "Comment identifier le registre d'un texte?",
            "Quelles sont les étapes pour résumer un texte?",
            "Analysez le ton de l'extrait suivant: 'Quelle catastrophe!'",
            "Expliquez pourquoi l'auteur utilise un registre familier dans un dialogue."
          ],
          examTips: "Pour les questions d'analyse, justifiez toujours votre réponse avec des citations du texte."
        }
      ]
    },
    strand3: {
      name: "Rédaction formelle",
      units: [
        {
          id: "ffl3-12",
          name: "Lettres officielles et informelles",
          objectives: [
            "Écrire des lettres formelles et informelles",
            "Utiliser le vocabulaire et format appropriés",
            "Maîtriser les formules de politesse"
          ],
          keyTerms: [
            { term: "La lettre officielle", definition: "Formal letter - written for business or official purposes" },
            { term: "La lettre informelle", definition: "Informal letter - written to friends or family" },
            { term: "L'en-tête", definition: "The header - includes address and date" },
            { term: "La formule d'appel", definition: "Greeting/salutation" },
            { term: "La formule de politesse", definition: "Closing formula" }
          ],
          studyNotes: `## Lettres en français

### Lettre officielle - Structure
\`\`\`
[Votre adresse]                    [Lieu, date]

[Adresse du destinataire]

Objet: [Sujet de la lettre]

Madame, Monsieur,

[Corps de la lettre...]

Je vous prie d'agréer, Madame, Monsieur,
l'expression de mes salutations distinguées.

[Signature]
[Nom]
\`\`\`

### Formules d'appel (Greetings)
| Officielle | Informelle |
|------------|------------|
| Madame, Monsieur, | Cher ami / Chère amie, |
| Monsieur le Directeur, | Salut! |
| Madame la Présidente, | Coucou! |

### Formules de politesse (Closings)
**Officielle:**
- "Je vous prie d'agréer mes salutations distinguées."
- "Veuillez accepter mes respectueuses salutations."

**Informelle:**
- "Amitiés"
- "À bientôt"
- "Bisous/Bises"
- "Cordialement"

### Types de lettres officielles
- Lettre de motivation (cover letter)
- Lettre de réclamation (complaint)
- Lettre de demande (request)
- Lettre de remerciement (thank you)`,
          practiceQuestions: [
            "Quelle est la structure d'une lettre officielle?",
            "Donnez trois formules de politesse pour une lettre officielle.",
            "Quelle formule d'appel utilise-t-on pour écrire à un ami?",
            "Écrivez une lettre de motivation pour un emploi d'été.",
            "Quelle est la différence de ton entre une lettre officielle et informelle?"
          ],
          examTips: "Mémorisez les formules de politesse officielles - elles sont essentielles pour l'examen."
        }
      ]
    },
    strand4: {
      name: "Grammaire avancée",
      units: [
        {
          id: "ffl4-12",
          name: "Syntaxe et structures complexes",
          objectives: [
            "Construire des phrases complexes",
            "Utiliser les voix active et passive",
            "Maîtriser les pronoms relatifs"
          ],
          keyTerms: [
            { term: "La voix active", definition: "Active voice - subject does the action" },
            { term: "La voix passive", definition: "Passive voice - subject receives the action" },
            { term: "Le pronom relatif", definition: "Relative pronoun - qui, que, dont, où" },
            { term: "La proposition subordonnée", definition: "Subordinate clause" },
            { term: "Le conditionnel", definition: "Conditional tense - what would happen" }
          ],
          studyNotes: `## Structures avancées

### La voix passive
**Formation:** être (conjugué) + participe passé

**Actif → Passif:**
- "Le chat mange la souris." → "La souris est mangée par le chat."
- "On a construit cette maison." → "Cette maison a été construite."

### Les pronoms relatifs
| Pronom | Usage | Exemple |
|--------|-------|---------|
| qui | sujet | L'homme qui parle... |
| que | objet direct | Le livre que je lis... |
| dont | complément avec "de" | La fille dont je parle... |
| où | lieu ou temps | La ville où j'habite... |

### Le conditionnel
**Formation:** Radical du futur + terminaisons de l'imparfait

**Exemple avec "parler":**
je parlerais, tu parlerais, il/elle parlerait
nous parlerions, vous parleriez, ils/elles parleraient

**Usage:**
- Politesse: "Je voudrais..." (I would like...)
- Hypothèse: "Si j'avais..., je ferais..." (If I had..., I would do...)
- Conseil: "Tu devrais..." (You should...)

### Les phrases complexes
- **Cause:** "Comme il pleut, je reste à la maison."
- **Conséquence:** "Il pleut, donc je reste à la maison."
- **But:** "Je travaille pour que tu réussisses."
- **Concession:** "Bien qu'il pleuve, je sors."`,
          practiceQuestions: [
            "Transformez à la voix passive: 'Le professeur corrige les copies.'",
            "Complétez avec qui, que, dont ou où: 'C'est le film ____ j'ai vu hier.'",
            "Conjuguez 'venir' au conditionnel présent.",
            "Quand utilise-t-on le conditionnel de politesse?",
            "Combinez ces phrases avec un pronom relatif: 'J'ai un ami. Cet ami habite à Paris.'"
          ],
          examTips: "Pratiquez la transformation actif-passif. Attention à l'accord du participe passé avec être."
        }
      ]
    }
  }
};

export const year13FrenchStudentNotes = {
  year: 13,
  subject: "French as a Foreign Language",
  strands: {
    strand1: {
      name: "Expression orale experte",
      units: [
        {
          id: "ffl1-13",
          name: "Débat et persuasion",
          objectives: [
            "Utiliser un langage persuasif efficacement",
            "Participer à des débats formels complexes",
            "Défendre et justifier des opinions"
          ],
          keyTerms: [
            { term: "La persuasion", definition: "Persuasion - convincing others to accept a viewpoint" },
            { term: "L'argumentation", definition: "Argumentation - presenting reasons to support a claim" },
            { term: "La réfutation", definition: "Refutation - disproving an opposing argument" },
            { term: "La rhétorique", definition: "Rhetoric - the art of effective speaking" },
            { term: "La thèse", definition: "Thesis - the main argument or position" }
          ],
          studyNotes: `## L'art du débat

### Structure d'un argument
1. **Thèse** - Votre position principale
2. **Arguments** - Raisons qui soutiennent votre thèse
3. **Exemples** - Preuves concrètes
4. **Conclusion** - Réaffirmation de votre position

### Techniques de persuasion
- **L'appel à la logique:** Utilisez des faits et statistiques
- **L'appel aux émotions:** Touchez le cœur de l'auditoire
- **L'appel à l'autorité:** Citez des experts
- **L'exemple personnel:** Partagez une expérience

### Expressions pour argumenter
**Introduire un argument:**
- "Premièrement, il faut souligner que..."
- "Un argument majeur est que..."
- "Il est indéniable que..."

**Ajouter un argument:**
- "De plus, on peut noter que..."
- "En outre, il convient de remarquer..."
- "Par ailleurs..."

**Réfuter:**
- "Cette affirmation est contestable car..."
- "Au contraire, les faits montrent que..."
- "Cependant, on pourrait objecter que..."

**Conclure:**
- "En définitive..."
- "En somme..."
- "Pour toutes ces raisons..."`,
          practiceQuestions: [
            "Quelles sont les quatre parties d'un argument?",
            "Donnez trois techniques de persuasion.",
            "Comment réfuter poliment un argument adverse?",
            "Préparez un discours persuasif sur l'environnement.",
            "Quelles expressions utilise-t-on pour conclure un débat?"
          ],
          examTips: "Anticipez les contre-arguments et préparez vos réponses à l'avance."
        }
      ]
    },
    strand2: {
      name: "Lecture critique",
      units: [
        {
          id: "ffl2-13",
          name: "Analyse critique",
          objectives: [
            "Analyser le style et l'intention de l'auteur",
            "Identifier le ton et l'effet recherché",
            "Comparer différentes perspectives"
          ],
          keyTerms: [
            { term: "L'intention de l'auteur", definition: "Author's intent - what the writer wants to achieve" },
            { term: "Le style", definition: "Style - the distinctive way an author writes" },
            { term: "Les figures de style", definition: "Figures of speech - literary devices" },
            { term: "L'ironie", definition: "Irony - saying one thing but meaning another" },
            { term: "La métaphore", definition: "Metaphor - comparing things without 'like' or 'as'" }
          ],
          studyNotes: `## Analyse critique de texte

### Les figures de style principales
| Figure | Définition | Exemple |
|--------|------------|---------|
| Métaphore | Comparaison implicite | "La vie est un voyage" |
| Comparaison | Avec "comme" | "Fort comme un lion" |
| Hyperbole | Exagération | "Je meurs de faim" |
| Ironie | Dire le contraire | "Bravo!" (sarcastique) |
| Personnification | Attribuer des qualités humaines | "Le vent murmure" |

### Analyser l'intention de l'auteur
Questions à poser:
- Quel est le but de ce texte? (informer, persuader, divertir)
- Quel effet veut-il produire sur le lecteur?
- Pourquoi a-t-il choisi ce style/ce vocabulaire?

### Les différents tons
- **Ironique:** Critique avec humour
- **Lyrique:** Expressif, émotionnel
- **Polémique:** Controversé, provocateur
- **Didactique:** Éducatif, instructif
- **Satirique:** Critique par la moquerie

### Comparer des points de vue
- "L'auteur A affirme que..., tandis que l'auteur B soutient..."
- "Contrairement à X, Y pense que..."
- "Les deux auteurs s'accordent sur... mais divergent sur..."`,
          practiceQuestions: [
            "Qu'est-ce qu'une métaphore? Donnez un exemple.",
            "Comment peut-on identifier le ton d'un texte?",
            "Analysez l'intention de l'auteur dans une publicité.",
            "Comparez deux articles sur le même sujet.",
            "Identifiez les figures de style: 'Le temps s'envole comme un oiseau.'"
          ],
          examTips: "Citez toujours le texte pour appuyer votre analyse. Utilisez des guillemets et indiquez les lignes."
        }
      ]
    },
    strand3: {
      name: "Rédaction experte",
      units: [
        {
          id: "ffl3-13",
          name: "L'essai argumentatif",
          objectives: [
            "Rédiger des essais argumentatifs complets",
            "Défendre un point de vue avec des preuves",
            "Structurer une argumentation cohérente"
          ],
          keyTerms: [
            { term: "L'essai", definition: "Essay - a piece of writing on a particular subject" },
            { term: "La problématique", definition: "The central question or issue to be addressed" },
            { term: "L'antithèse", definition: "Antithesis - the opposing argument" },
            { term: "La synthèse", definition: "Synthesis - combining different views" },
            { term: "La transition", definition: "Transition - link between paragraphs" }
          ],
          studyNotes: `## L'essai argumentatif

### Plan dialectique (Thèse - Antithèse - Synthèse)
1. **Introduction**
   - Accroche (citation, question, fait)
   - Présentation du sujet
   - Problématique
   - Annonce du plan

2. **Thèse** (Arguments pour)
   - Argument 1 + exemple
   - Argument 2 + exemple

3. **Antithèse** (Arguments contre)
   - Argument 1 + exemple
   - Argument 2 + exemple

4. **Synthèse** (Vision nuancée)
   - Dépassement des oppositions
   - Position personnelle

5. **Conclusion**
   - Bilan
   - Ouverture

### Expressions pour les transitions
**De la thèse à l'antithèse:**
- "Cependant, on peut objecter que..."
- "Néanmoins, certains pensent que..."
- "Toutefois, il existe un autre point de vue..."

**Pour la synthèse:**
- "En fin de compte..."
- "Finalement, il apparaît que..."
- "Une position nuancée serait de..."

### Conseils pour l'essai
1. Analysez bien le sujet avant d'écrire
2. Faites un plan détaillé
3. Un paragraphe = une idée + un exemple
4. Utilisez des connecteurs logiques
5. Relisez-vous pour corriger les erreurs`,
          practiceQuestions: [
            "Quelles sont les cinq parties d'un essai argumentatif?",
            "Qu'est-ce qu'une problématique?",
            "Comment passer de la thèse à l'antithèse?",
            "Rédigez une introduction sur le sujet: 'Les réseaux sociaux sont-ils bénéfiques?'",
            "Quelle est la différence entre antithèse et synthèse?"
          ],
          examTips: "Gérez votre temps: 5 min pour analyser, 10 min pour planifier, 30 min pour écrire, 5 min pour relire."
        },
        {
          id: "ffl3-13b",
          name: "Description culturelle",
          objectives: [
            "Décrire la vie dans les pays francophones",
            "Comparer les cultures",
            "Analyser les similitudes et différences"
          ],
          keyTerms: [
            { term: "La Francophonie", definition: "French-speaking world - countries where French is spoken" },
            { term: "Le mode de vie", definition: "Lifestyle - the way people live" },
            { term: "Les traditions", definition: "Traditions - customs passed down through generations" },
            { term: "L'identité culturelle", definition: "Cultural identity" },
            { term: "Le patrimoine", definition: "Heritage - cultural inheritance" }
          ],
          studyNotes: `## Les pays francophones

### La Francophonie
Plus de 300 millions de personnes parlent français dans le monde:
- **Europe:** France, Belgique, Suisse, Luxembourg, Monaco
- **Afrique:** Sénégal, Côte d'Ivoire, Cameroun, etc.
- **Amérique:** Canada (Québec), Haïti
- **Océanie:** Nouvelle-Calédonie, Polynésie française
- **Pacifique:** Vanuatu!

### Comparer les cultures
| Aspect | Vanuatu | France |
|--------|---------|--------|
| Climat | Tropical | Tempéré |
| Cuisine | Lap-lap, poisson | Baguette, fromage |
| Langues | Bislama, français, anglais | Français |
| Fêtes | Indépendance (30 juillet) | 14 juillet |

### Expressions pour comparer
**Similitudes:**
- "Comme en France, au Vanuatu..."
- "De même que..."
- "Tout comme..."

**Différences:**
- "Contrairement à..."
- "À la différence de..."
- "Alors que... en revanche..."

### Aspects à comparer
- La nourriture et la gastronomie
- Les fêtes et traditions
- La vie quotidienne
- Les valeurs familiales
- L'éducation
- Les sports et loisirs`,
          practiceQuestions: [
            "Nommez cinq pays francophones de différents continents.",
            "Comparez une tradition du Vanuatu avec une tradition française.",
            "Quelles expressions utilise-t-on pour montrer des similitudes?",
            "Décrivez le mode de vie dans un pays francophone de votre choix.",
            "Comment la culture du Vanuatu est-elle unique dans la Francophonie?"
          ],
          examTips: "Montrez que vous connaissez plusieurs pays francophones, pas seulement la France."
        }
      ]
    },
    strand4: {
      name: "Grammaire experte",
      units: [
        {
          id: "ffl4-13",
          name: "Le subjonctif et le discours indirect",
          objectives: [
            "Maîtriser le subjonctif présent",
            "Transformer le discours direct en discours indirect",
            "Appliquer la concordance des temps"
          ],
          keyTerms: [
            { term: "Le subjonctif", definition: "Subjunctive mood - used for wishes, doubts, emotions" },
            { term: "Le discours direct", definition: "Direct speech - quoting exact words" },
            { term: "Le discours indirect", definition: "Indirect/reported speech" },
            { term: "La concordance des temps", definition: "Sequence of tenses - matching tenses correctly" }
          ],
          studyNotes: `## Grammaire avancée

### Le subjonctif présent
**Formation:** Radical du présent (ils) + -e, -es, -e, -ions, -iez, -ent

**Exemple (parler):**
que je parle, que tu parles, qu'il/elle parle
que nous parlions, que vous parliez, qu'ils/elles parlent

**Verbes irréguliers courants:**
- être: que je sois
- avoir: que j'aie
- aller: que j'aille
- faire: que je fasse
- pouvoir: que je puisse

**Usage du subjonctif:**
Après des expressions de:
- Souhait: "Je veux que tu viennes"
- Doute: "Je doute qu'il soit là"
- Émotion: "Je suis content qu'elle réussisse"
- Nécessité: "Il faut que nous partions"

### Le discours indirect
**Direct → Indirect:**
| Direct | Indirect |
|--------|----------|
| "Je pars" | Il dit qu'il part |
| "Je suis parti" | Il dit qu'il est parti |
| "Je partirai" | Il dit qu'il partira |

**Avec verbe au passé (concordance):**
| Direct | Indirect |
|--------|----------|
| "Je pars" | Il a dit qu'il partait |
| "Je suis parti" | Il a dit qu'il était parti |
| "Je partirai" | Il a dit qu'il partirait |

**Changements:**
- je → il/elle
- mon/ma → son/sa
- ici → là
- aujourd'hui → ce jour-là
- demain → le lendemain`,
          practiceQuestions: [
            "Conjuguez 'finir' au subjonctif présent.",
            "Quand utilise-t-on le subjonctif?",
            "Transformez au discours indirect: 'Marie dit: \"J'ai faim.\"'",
            "Transformez avec verbe au passé: 'Pierre a dit: \"Je viendrai demain.\"'",
            "Complétez avec l'indicatif ou le subjonctif: 'Je sais qu'elle ____ (être) là.'"
          ],
          examTips: "Mémorisez les verbes/expressions qui déclenchent le subjonctif. Le subjonctif s'utilise après 'que' + doute/émotion/souhait."
        }
      ]
    }
  }
};

export default {
  year11FrenchStudentNotes,
  year12FrenchStudentNotes,
  year13FrenchStudentNotes
};
